// Critical resource preloader
export class ResourcePreloader {
  private static instance: ResourcePreloader;
  private preloadedResources = new Set<string>();

  public static getInstance(): ResourcePreloader {
    if (!ResourcePreloader.instance) {
      ResourcePreloader.instance = new ResourcePreloader();
    }
    return ResourcePreloader.instance;
  }

  // Preload critical CSS (immediate=true) or prefetch as a hint (default)
  public preloadCSS(href: string, immediate = false): void {
    if (this.preloadedResources.has(href)) return;

    if (immediate) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      this.preloadedResources.add(href);
      return;
    }

    // Speculative: prefer prefetch to avoid "preload not used" warnings
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'style' as any;
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    this.preloadedResources.add(href);
  }

  // Preload JavaScript modules (expectSoon=true uses modulepreload, else prefetch)
  public preloadModule(href: string, expectSoon = false): void {
    if (this.preloadedResources.has(href)) return;

    const link = document.createElement('link');
    link.rel = expectSoon ? 'modulepreload' : 'prefetch';
    if (!expectSoon) {
      (link as any).as = 'script';
    }
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    this.preloadedResources.add(href);

    // If we expected soon, but it's not used quickly, downgrade to prefetch to avoid warnings
    if (expectSoon) {
      setTimeout(() => {
        // If still in DOM with modulepreload, replace by prefetch hint
        if (link.isConnected && link.rel === 'modulepreload') {
          const prefetch = document.createElement('link');
          prefetch.rel = 'prefetch';
          (prefetch as any).as = 'script';
          prefetch.href = href;
          prefetch.crossOrigin = 'anonymous';
          try { link.replaceWith(prefetch); } catch {}
        }
      }, 5000);
    }
  }

  // Preload images with priority
  public preloadImage(src: string, priority: 'high' | 'low' = 'low'): Promise<void> {
    if (this.preloadedResources.has(src)) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.preloadedResources.add(src);
        resolve();
      };
      img.onerror = reject;
      img.src = src;
      
      if (priority === 'high') {
        img.loading = 'eager';
      }
    });
  }

  // Preload route chunks (expectSoon: true for imminent navigation)
  public preloadRoute(routePath: string, expectSoon = false): void {
    const routeMap: Record<string, string[]> = {
      '/formations': ['formations', 'ui-vendor'],
      '/dashboard': ['dashboard', 'query-vendor'],
      '/admin': ['admin', 'ui-vendor'],
      '/subscriptions': ['payments', 'ui-vendor'],
    };

    const chunks = routeMap[routePath];
    if (chunks) {
      chunks.forEach(chunk => {
        const url = `/assets/${chunk}.js`;
        this.preloadModule(url, expectSoon);
      });
    }
  }

  // DNS prefetch for external domains
  public prefetchDNS(domain: string): void {
    if (this.preloadedResources.has(`dns:${domain}`)) return;
    
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
    this.preloadedResources.add(`dns:${domain}`);
  }

  // Preconnect to critical origins
  public preconnect(origin: string): void {
    if (this.preloadedResources.has(`preconnect:${origin}`)) return;
    
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    this.preloadedResources.add(`preconnect:${origin}`);
  }
}

// Initialize critical preloads
export const initCriticalPreloads = () => {
  const preloader = ResourcePreloader.getInstance();
  
  // Preconnect to Supabase
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (supabaseUrl) {
    preloader.preconnect(supabaseUrl);
  }
  
  // DNS prefetch for common domains
  preloader.prefetchDNS('fonts.googleapis.com');
  preloader.prefetchDNS('cdn.jsdelivr.net');
};
